import { useState, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { supabase } from '@/shared/lib/supabase'

export interface Message {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: Date
}

export interface Suggestion {
  field: string
  content: string
}

interface AIResponse {
  response: string
  suggestions?: Suggestion[]
}

interface UseAIAssistantOptions {
  onSuggestions?: (suggestions: Suggestion[]) => void
}

export function useAIAssistant(options?: UseAIAssistantOptions) {
  const { i18n } = useTranslation()
  const [messages, setMessages] = useState<Message[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [currentSuggestions, setCurrentSuggestions] = useState<Suggestion[]>([])
  const [requestCount, setRequestCount] = useState(0)

  const MAX_REQUESTS = 30
  const isRateLimited = requestCount >= MAX_REQUESTS

  // Get welcome message on mount
  useEffect(() => {
    const getWelcomeMessage = async () => {
      setIsLoading(true)
      setError(null)

      try {
        const { data, error: fnError } = await supabase.functions.invoke<AIResponse>(
          'ai-precadrage-assistant',
          {
            body: {
              messages: [],
              language: i18n.language === 'fr' ? 'fr' : 'en',
            },
          }
        )

        if (fnError) {
          throw new Error(fnError.message)
        }

        if (data?.response) {
          const welcomeMessage: Message = {
            id: crypto.randomUUID(),
            role: 'assistant',
            content: data.response,
            timestamp: new Date(),
          }
          setMessages([welcomeMessage])
        }
      } catch (err) {
        console.error('Error getting welcome message:', err)
        // Don't show error for welcome message, just use a fallback
        const fallbackMessage: Message = {
          id: crypto.randomUUID(),
          role: 'assistant',
          content:
            i18n.language === 'fr'
              ? "Bonjour ! 👋 Je suis là pour vous aider à décrire votre projet. Parlez-moi de votre idée !"
              : "Hello! 👋 I'm here to help you describe your project. Tell me about your idea!",
          timestamp: new Date(),
        }
        setMessages([fallbackMessage])
      } finally {
        setIsLoading(false)
      }
    }

    getWelcomeMessage()
  }, [i18n.language])

  const sendMessage = useCallback(
    async (content: string) => {
      if (!content.trim() || isLoading || isRateLimited) return

      setError(null)
      setCurrentSuggestions([])

      // Add user message
      const userMessage: Message = {
        id: crypto.randomUUID(),
        role: 'user',
        content: content.trim(),
        timestamp: new Date(),
      }
      setMessages((prev) => [...prev, userMessage])
      setIsLoading(true)

      try {
        // Prepare conversation history for API
        const conversationHistory = [...messages, userMessage].map((msg) => ({
          role: msg.role as 'user' | 'assistant',
          content: msg.content,
        }))

        const { data, error: fnError } = await supabase.functions.invoke<AIResponse>(
          'ai-precadrage-assistant',
          {
            body: {
              messages: conversationHistory,
              language: i18n.language === 'fr' ? 'fr' : 'en',
            },
          }
        )

        if (fnError) {
          throw new Error(fnError.message)
        }

        setRequestCount((prev) => prev + 1)

        if (data?.response) {
          const assistantMessage: Message = {
            id: crypto.randomUUID(),
            role: 'assistant',
            content: data.response,
            timestamp: new Date(),
          }
          setMessages((prev) => [...prev, assistantMessage])

          // Handle suggestions
          if (data.suggestions && data.suggestions.length > 0) {
            setCurrentSuggestions(data.suggestions)
            options?.onSuggestions?.(data.suggestions)
          }
        }
      } catch (err) {
        console.error('Error sending message:', err)
        setError(
          i18n.language === 'fr'
            ? "Une erreur s'est produite. Veuillez réessayer."
            : 'An error occurred. Please try again.'
        )
      } finally {
        setIsLoading(false)
      }
    },
    [messages, isLoading, isRateLimited, i18n.language, options]
  )

  const clearSuggestion = useCallback((index?: number) => {
    if (index !== undefined) {
      setCurrentSuggestions((prev) => prev.filter((_, i) => i !== index))
    } else {
      setCurrentSuggestions([])
    }
  }, [])

  const retry = useCallback(() => {
    setError(null)
    // Get the last user message and resend
    const lastUserMessage = [...messages].reverse().find((m) => m.role === 'user')
    if (lastUserMessage) {
      // Remove it and resend
      setMessages((prev) => prev.slice(0, -1))
      sendMessage(lastUserMessage.content)
    }
  }, [messages, sendMessage])

  return {
    messages,
    isLoading,
    error,
    currentSuggestions,
    isRateLimited,
    requestCount,
    maxRequests: MAX_REQUESTS,
    sendMessage,
    clearSuggestion,
    retry,
  }
}
